import React from "react"

import {useAccessToken} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {
	ManualReviewInsights,
	getManualReviewInsights,
	getSharedElementsInsights,
	getWebsiteInsights,
	SharedElementsInsights,
	WebsiteInsights,
} from "../../resources/application"
import {AiInsightsView, Insight} from "./AiInsightsView"
import {ManualReviewRecommendation} from "./ManualReviewRecommendation"

interface AiInsightsProps {
	close: () => void
	applicationId: string
}

interface InsightsData {
	sharedElements?: SharedElementsInsights
	manualReview?: ManualReviewInsights
	website?: WebsiteInsights
	sharedElementsError?: boolean
	manualReviewError?: boolean
	websiteError?: boolean
}

export const convertToAiInsightsView = (data: InsightsData) => {
	const {sharedElements, manualReview, website, sharedElementsError, manualReviewError, websiteError} = data

	const items: Insight[] = []

	if (!manualReviewError && manualReview) {
		items.push({
			title: "Manual Review",
			description: <ManualReviewRecommendation data={manualReview} />,
		})
	}

	if (!websiteError && website) {
		items.push({
			title: "Business Website",
			description: website.attributes.short_summary,
		})
	}

	if (!sharedElementsError && sharedElements) {
		items.push({
			title: "Shared Elements",
			description: <pre className="text-wrap-auto">{JSON.stringify(sharedElements.attributes)}</pre>,
		})
	}

	return items
}

export function AiInsights({close, applicationId}: AiInsightsProps) {
	const accessToken = useAccessToken()
	const sharedElementsInsights = useAsyncResult(() => getSharedElementsInsights(accessToken, applicationId))
	const manualReviewInsights = useAsyncResult(() => getManualReviewInsights(accessToken, applicationId))
	const websiteInsights = useAsyncResult(() => getWebsiteInsights(accessToken, applicationId))

	const isLoading =
		sharedElementsInsights.match(
			() => true,
			() => false,
			() => false
		) ||
		manualReviewInsights.match(
			() => true,
			() => false,
			() => false
		) ||
		websiteInsights.match(
			() => true,
			() => false,
			() => false
		)

	if (isLoading) {
		return <AiInsightsView loading={true} close={close} />
	}

	const sharedElements = sharedElementsInsights.match(
		() => undefined,
		(result) => (result.data.type === "noResponse" ? undefined : result.data),
		() => undefined
	)
	const sharedElementsError = sharedElementsInsights.match(
		() => false,
		(result) => result.data.type === "noResponse",
		() => true
	)

	const manualReview = manualReviewInsights.match(
		() => undefined,
		(result) => (result.data.type === "noResponse" ? undefined : result.data),
		() => undefined
	)
	const manualReviewError = manualReviewInsights.match(
		() => false,
		(result) => result.data.type === "noResponse",
		() => true
	)

	const website = websiteInsights.match(
		() => undefined,
		(result) => (result.data.type === "noResponse" ? undefined : result.data),
		() => undefined
	)
	const websiteError = websiteInsights.match(
		() => false,
		(result) => result.data.type === "noResponse",
		() => true
	)

	if (sharedElementsError && manualReviewError && websiteError) {
		return <AiInsightsView error close={close} />
	}

	return (
		<AiInsightsView
			close={close}
			items={convertToAiInsightsView({
				sharedElements,
				manualReview,
				website,
				sharedElementsError,
				manualReviewError,
				websiteError,
			})}
		/>
	)
}
